import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Flex, message, Row, Spin } from "antd";
import { useLocation, useNavigate } from "react-router";
import { ArrowLeftOutlined } from "@ant-design/icons";
import ModalDetail from "./modal-detail";
import "./style.css";
import API from "../../../services/api-request";

const DetailStock = () => {
  const [selectedStorage, setSelectedStorage] = useState(null);
  const [selectedRack, setSelectedRack] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stockState, setStockState] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Grab state from location or sessionStorage
  useEffect(() => {
    let state = location.state;
    let fromSession = false;

    if (!state) {
      const stored = sessionStorage.getItem("stockDetailState");
      if (stored) {
        state = JSON.parse(stored);
        fromSession = true;

        const nameParts = state?.name?.split("-");

        const rowNumber = nameParts?.[1]?.replace(/\D/g, "");
        const sectionLetter = nameParts?.[1]?.replace(/\d/g, "");

        state.id = Number.parseInt(state?.warehouse_id);
        state.row = Number.parseInt(rowNumber);
        state.section = sectionLetter;

        // Optionally clear sessionStorage
        sessionStorage.removeItem("stockDetailState");
      }
    }

    setStockState({ ...state, fromSession });
  }, [location.state]);

  const id = stockState?.id;
  const row_number = stockState?.row;
  const section = stockState?.section;
  const name = stockState?.name;

  const getAreaData = useCallback(() => {
    setLoading(true);

    API.Get(`area/map?warehouseId=${id}`)
      .then((res) => {
        const data = res?.data;

        const findStorage = data?.[0]?.rows?.find(
          (row) => row.row_number === row_number,
        );

        const matchedSection = findStorage?.sections?.find(
          (sec) => sec.section === section,
        );

        setSelectedStorage(matchedSection);
      })
      .catch((err) => {
        console.log(err);
        message.error("Failed to fetch area data!");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, row_number, section]);

  useEffect(() => {
    if (!id || !row_number || !section) return;

    getAreaData();
  }, [getAreaData, id, row_number, section]);

  useEffect(() => {
    if (!stockState?.fromSession || !selectedStorage) return;

    // Find the rack from stockState
    const column = selectedStorage.columns.find(
      (col) => col.column_name === stockState.column_name,
    );
    const rack = column?.levels.find((r) => r.level === stockState.level);

    if (rack?.status === "occupied") {
      setSelectedRack(rack);
      setOpenModal(true);
    }
  }, [stockState, selectedStorage]);

  const handleOpenModal = (value) => {
    setSelectedRack(value);

    if (value.status === "occupied") {
      setOpenModal(true);
    } else {
      message.info("Selected rack is empty.");
    }
  };

  const handleOk = () => {
    setOpenModal(false);
  };

  const handleCancel = () => {
    setOpenModal(false);
  };

  return (
    <>
      <Button
        style={{ fontSize: "15px", color: "#8c878b" }}
        icon={<ArrowLeftOutlined />}
        color="default"
        variant="text"
        onClick={() => navigate("/stock-opname")}
      >
        Back to Stock Opname
      </Button>

      {loading ? (
        <Flex align="center" justify="center" style={{ marginTop: "10rem" }}>
          <Spin size="large" />
        </Flex>
      ) : (
        <Flex justify="center" align="center">
          {((selectedStorage?.columns ?? [])
            .slice()
            .sort((a, b) => (selectedStorage?.section === "A" ? -1 : 1))
          )
            ?.reduce((acc, column) => {
              if (column.column_name === "S") {
                return [column, ...acc];
              }
              return [...acc, column];
            }, [])
            ?.map((column, colIdx) => (
              <Row gutter={0} key={column?.column_name || colIdx}>
                <Col span={24}>
                  {column?.levels
                    ?.slice()
                    .reverse()
                    .map((rack, rackIdx) => {
                      let rackClass = "rack-unavailable";
                      if (column.column_name === "T") {
                        rackClass = "rack-temporary";
                      } else if (rack?.status === "empty") {
                        rackClass = "rack-available";
                      }

                      return (
                        <div style={{ width: "100%" }} key={rack?.id || rack?.level || rackIdx}>
                          <Button
                            variant="outlined"
                            className={`rack ${rackClass}`}
                            onClick={() =>
                              handleOpenModal({
                                ...rack,
                                column: column?.column_name,
                              })
                            }
                          >
                          <Row gutter={0} style={{ width: "6rem" }}>
                            <Col span={24} style={{ fontSize: "24px" }}>
                              {rack.level}
                            </Col>
                            {rack.status === "occupied" && (
                              <>
                                <Col span={24} style={{ fontWeight: "normal" }}>
                                  {rack.pallet_name}
                                </Col>
                                <Col span={24} style={{ fontWeight: "normal" }}>
                                  {rack.client_name}
                                </Col>
                                <Col span={24} style={{ fontWeight: "normal" }}>
                                  {Math.round(rack.total_weight)} Kg
                                </Col>
                              </>
                            )}
                          </Row>
                        </Button>
                      </div>
                      );
                    })}
                </Col>
                <Col span={24}>
                  <p
                    style={{
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "1.5rem",
                    }}
                  >
                    {column.column_name}
                  </p>
                </Col>
              </Row>
            ))}
          {!name?.includes("T-") && (
            <Flex vertical style={{ marginLeft: "1%" }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <div
                  style={{
                    width: "1rem",
                    height: "1rem",
                    backgroundColor: "#d32526",
                    display: "inline-block",
                    borderRadius: "5px",
                  }}
                />
                <span> Unavailable</span>
              </div>
              <br />
              <div
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <div
                  style={{
                    width: "1rem",
                    height: "1rem",
                    backgroundColor: "#26d3a6",
                    display: "inline-block",
                    borderRadius: "5px",
                  }}
                />
                <span> Available</span>
              </div>
            </Flex>
          )}
        </Flex>
      )}

      {/* Modal */}
      <ModalDetail
        open={openModal}
        data={selectedRack}
        onCancel={handleCancel}
        onOk={handleOk}
        location={location}
        getAreaData={getAreaData}
      />
    </>
  );
};


DetailStock.propTypes = {

};

export default DetailStock;
